/**
 * PDFCalibrationGrid — overlay CSS affichant une grille de guides sur les
 * outils de calibration PDF.
 *
 * Utilise un background linear-gradient CSS (plus fiable qu'un SVG dans un
 * conteneur en overflow scroll). Lignes fines tous les `step` pt, lignes
 * épaisses tous les `majorStep` pt.
 *
 * Props :
 *   • pdfWidth, pdfHeight : taille du PDF en points (ex. 612 × 792)
 *   • scale              : facteur d'affichage image → pixels (ex. 2)
 *   • zoom               : zoom utilisateur (ex. 1, 1.5, 2)
 *   • step               : pas de la grille en points (défaut 10)
 *   • majorStep          : pas des lignes épaisses en points (défaut 50)
 */
export default function PDFCalibrationGrid({
  pdfWidth,
  pdfHeight,
  scale = 2,
  zoom = 1,
  step = 10,
  majorStep = 50,
}) {
  const w = pdfWidth * scale * zoom;
  const h = pdfHeight * scale * zoom;
  const cell = step * scale * zoom;       // Taille en pixels d'une cellule
  const majorCell = majorStep * scale * zoom;

  // Couleurs franches par-dessus du blanc.
  const MINOR = 'rgba(148,163,184,0.55)';   // slate-400 translucide
  const MAJOR = 'rgba(29,78,216,0.75)';     // blue-700

  const bg = [
    // Lignes verticales majeures
    `repeating-linear-gradient(to right, ${MAJOR} 0 1.5px, transparent 1.5px ${majorCell}px)`,
    // Lignes verticales mineures
    `repeating-linear-gradient(to right, ${MINOR} 0 1px, transparent 1px ${cell}px)`,
    // Lignes horizontales majeures
    `repeating-linear-gradient(to bottom, ${MAJOR} 0 1.5px, transparent 1.5px ${majorCell}px)`,
    // Lignes horizontales mineures
    `repeating-linear-gradient(to bottom, ${MINOR} 0 1px, transparent 1px ${cell}px)`,
  ].join(', ');

  // Étiquettes 50/100/150/... sur les axes X et Y
  const labels = [];
  for (let x = majorStep; x < pdfWidth; x += majorStep) {
    labels.push(
      <div key={`lx-${x}`} style={{
        position: 'absolute', top: 2, left: x * scale * zoom + 3,
        fontFamily: 'monospace', fontSize: Math.max(10, 10 * zoom),
        color: '#1d4ed8', fontWeight: 600, pointerEvents: 'none',
        textShadow: '0 0 3px #fff, 0 0 3px #fff'
      }}>{x}</div>
    );
  }
  for (let y = majorStep; y < pdfHeight; y += majorStep) {
    labels.push(
      <div key={`ly-${y}`} style={{
        position: 'absolute', left: 2, top: y * scale * zoom - 6,
        fontFamily: 'monospace', fontSize: Math.max(10, 10 * zoom),
        color: '#1d4ed8', fontWeight: 600, pointerEvents: 'none',
        textShadow: '0 0 3px #fff, 0 0 3px #fff'
      }}>{y}</div>
    );
  }

  return (
    <div data-testid="pdf-cal-grid"
      style={{
        position: 'absolute', top: 0, left: 0,
        width: w, height: h,
        pointerEvents: 'none',
        zIndex: 2,
        backgroundImage: bg,
        backgroundSize: `${majorCell}px ${majorCell}px, ${cell}px ${cell}px, ${majorCell}px ${majorCell}px, ${cell}px ${cell}px`,
        backgroundPosition: '0 0, 0 0, 0 0, 0 0',
      }}>
      {labels}
    </div>
  );
}
